// ============================================================================
// formatMoney — formato de plata CENTRAL de la app (oct-2026).
//
// Reemplaza los `$${x.toLocaleString('es-CO')}` que estaban regados por los
// componentes. La moneda la define el backend (GET /api/regional, una por
// instalación); el RegionalProvider la fija acá con setMonedaActual().
//
// GARANTÍA con COP (la moneda de siempre): formatMoney(x, opts) devuelve
// EXACTAMENTE '$' + x.toLocaleString('es-CO', opts), sobre el MISMO valor y sin
// convertirlo. El texto en pantalla queda idéntico carácter por carácter
// (lo fija moneda.test.ts).
//
// Fuera de COP usa Intl con la moneda y los decimales del registro. Los
// montos NO se convierten: el número guardado se muestra con otra etiqueta.
// ============================================================================

export interface MonedaConfig {
  codigo: string;
  nombre: string;
  locale: string;
  decimales: number;
  operaColombia: boolean;
}

export const MONEDA_COP: MonedaConfig = Object.freeze({
  codigo: 'COP', nombre: 'Peso colombiano', locale: 'es-CO', decimales: 0, operaColombia: true,
});

// Caché por navegador: así el primer render ya sale con la moneda correcta y
// no hay que esperar al backend. Con COP no se guarda nada (nada cambia).
const CLAVE_CACHE = 'salonika_moneda';

export function esMonedaConfig(m: unknown): m is MonedaConfig {
  const x = m as MonedaConfig;
  return !!x && typeof x === 'object'
    && typeof x.codigo === 'string' && /^[A-Z]{3}$/.test(x.codigo)
    && typeof x.locale === 'string' && x.locale.length > 0
    && typeof x.decimales === 'number' && x.decimales >= 0 && x.decimales <= 4
    && typeof x.operaColombia === 'boolean';
}

function leerCache(): MonedaConfig | null {
  try {
    const raw = localStorage.getItem(CLAVE_CACHE);
    if (!raw) return null;
    const m = JSON.parse(raw);
    return esMonedaConfig(m) ? m : null;
  } catch {
    return null;
  }
}

let actual: MonedaConfig = leerCache() ?? MONEDA_COP;

export function getMonedaActual(): MonedaConfig {
  return actual;
}

export function setMonedaActual(m: MonedaConfig): void {
  actual = esMonedaConfig(m) ? m : MONEDA_COP;
  try {
    if (actual.codigo === 'COP') localStorage.removeItem(CLAVE_CACHE);
    else localStorage.setItem(CLAVE_CACHE, JSON.stringify(actual));
  } catch { /* storage bloqueado: la moneda igual queda en memoria */ }
}

function intlFueraDeCOP(n: number, m: MonedaConfig): string {
  return new Intl.NumberFormat(m.locale, {
    style: 'currency', currency: m.codigo,
    minimumFractionDigits: m.decimales, maximumFractionDigits: m.decimales,
  }).format(n);
}

/**
 * Formatea un monto con la moneda de la instalación.
 * Con COP: idéntico a `'$' + valor.toLocaleString('es-CO', opts)`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formatMoney(valor: any, opts?: Intl.NumberFormatOptions): string {
  if (actual.codigo === 'COP') {
    return '$' + (opts === undefined ? valor.toLocaleString('es-CO') : valor.toLocaleString('es-CO', opts));
  }
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n)) return String(valor);
  return intlFueraDeCOP(n, actual);
}

/**
 * Para los dos helpers que usaban Intl con currency 'COP' ("$ 45.000", con
 * espacio). Con COP: idéntico a
 * `new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', ...opts })`.
 */
export function formatMoneyIntl(n: number, opts: Intl.NumberFormatOptions = {}): string {
  if (actual.codigo === 'COP') {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', ...opts }).format(n);
  }
  return intlFueraDeCOP(Number.isFinite(n) ? n : 0, actual);
}

/**
 * Para los sitios que redondeaban a pesos ANTES de formatear
 * (`'$' + Math.round(x).toLocaleString('es-CO')`). Con COP es idéntico; en
 * otras monedas no redondea a enteros (muestra los centavos).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formatMoneyRedondo(valor: any, opts?: Intl.NumberFormatOptions): string {
  if (actual.codigo === 'COP') return formatMoney(Math.round(valor), opts);
  return formatMoney(valor);
}

/**
 * Para los sitios que usaban `toLocaleString()` SIN locale (formato del
 * navegador). Con COP es idéntico a `'$' + valor.toLocaleString()`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formatMoneyNavegador(valor: any): string {
  if (actual.codigo === 'COP') return '$' + valor.toLocaleString();
  return formatMoney(valor);
}

/**
 * Símbolo de la moneda para formatos abreviados hechos a mano ("$1.2M",
 * "$45k"). Con COP es '$', exactamente como antes.
 */
/**
 * Cifra abreviada en millones (KPI y eje de Reportes). Con COP es exactamente
 * lo de siempre: '$' + (v / 1e6).toFixed(1) + 'M'. Con otra moneda las cifras
 * son mucho más chicas ($0.1M no dice nada): notación compacta de Intl ("$46.3K").
 */
export function formatMoneyMillones(v: number): string {
  if (actual.codigo === 'COP') return `$${(v / 1_000_000).toFixed(1)}M`;
  try {
    return new Intl.NumberFormat(actual.locale, {
      style: 'currency', currency: actual.codigo, notation: 'compact', maximumFractionDigits: 1,
    }).format(v);
  } catch {
    return formatMoney(v);
  }
}

/**
 * Nombre de una moneda en el idioma de la interfaz (Features). Con español se
 * usa el nombre del registro del backend, como siempre; con otro idioma, el
 * que da Intl ("US Dollar"), y si Intl no lo sabe, el del registro.
 */
export function nombreMoneda(m: { codigo: string; nombre: string }, idioma: string): string {
  if (idioma === 'es') return m.nombre;
  try {
    return new Intl.DisplayNames([idioma], { type: 'currency' }).of(m.codigo) || m.nombre;
  } catch {
    return m.nombre;
  }
}

export function simboloMoneda(): string {
  if (actual.codigo === 'COP') return '$';
  try {
    const p = new Intl.NumberFormat(actual.locale, { style: 'currency', currency: actual.codigo })
      .formatToParts(0).find(x => x.type === 'currency');
    return p ? p.value : actual.codigo;
  } catch {
    return actual.codigo;
  }
}

// ---------------------------------------------------------------------------
// Campos de PLATA (oct-2026).
// Con COP todo es idéntico a lo que hacía cada campo: solo dígitos y separador
// de miles es-CO. Con una moneda con centavos (USD/EUR) el campo acepta un
// separador decimal ('.' o ',') y muestra lo que se escribe, sin reformatear.
// ---------------------------------------------------------------------------

/** ¿La moneda de la instalación tiene centavos? COP: no (pesos enteros). */
export function monedaConDecimales(): boolean {
  return actual.decimales > 0;
}

/** Código ISO de la moneda de la instalación ('COP', 'USD'…). */
export function codigoMoneda(): string {
  return actual.codigo;
}

/** Limpia lo escrito en un campo de plata. COP: `texto.replace(/\D/g, '')`, como siempre. */
export function limpiarMonto(texto: string): string {
  if (!monedaConDecimales()) return texto.replace(/\D/g, '');
  let t = String(texto).replace(/,/g, '.').replace(/[^\d.]/g, '');
  const i = t.indexOf('.');
  if (i >= 0) t = t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '').slice(0, actual.decimales);
  return t;
}

/** Texto del campo a partir de lo guardado. COP: `Number(v).toLocaleString('es-CO')`, como siempre. */
export function montoEnCampo(v: string | number): string {
  if (!monedaConDecimales()) return Number(v).toLocaleString('es-CO');
  return String(v);
}

/** Teclado del celular para un campo de plata: 'numeric' con COP (como siempre), 'decimal' con centavos. */
export function tecladoMonto(): 'numeric' | 'decimal' {
  return monedaConDecimales() ? 'decimal' : 'numeric';
}

// ---------------------------------------------------------------------------
// Aritmética de plata (oct-2026). Con COP cada función es EXACTAMENTE la
// operación de siempre (Math.round / Math.floor a pesos). Con centavos
// redondea a los decimales de la moneda en vez de a enteros.
// ---------------------------------------------------------------------------
const factorDecimales = () => 10 ** actual.decimales;

/** Redondeo de un monto. COP: Math.round(x). */
export function redondearMoneda(x: number): number {
  if (!monedaConDecimales()) return Math.round(x);
  const f = factorDecimales();
  return Math.round((x + Number.EPSILON) * f) / f;
}

/** Piso de un monto. COP: Math.floor(x). */
export function pisoMoneda(x: number): number {
  if (!monedaConDecimales()) return Math.floor(x);
  const f = factorDecimales();
  return Math.floor((x + Number.EPSILON) * f) / f;
}

/**
 * Resto de una resta de montos (ej. "el último pagador absorbe el resto").
 * COP: x tal cual (no se toca). Con centavos: redondeado a centavos para que
 * 100 - 33.33 - 33.33 no dé 33.340000000000003.
 */
export function ajustarResto(x: number): number {
  if (!monedaConDecimales()) return x;
  return redondearMoneda(x);
}

/**
 * Propina redondeada hacia abajo. COP: al múltiplo de $100 (regla del negocio,
 * jun-2026): Math.floor(Math.max(0, v) / 100) * 100. Con centavos: al centavo.
 */
export function pisoPropina(v: number): number {
  if (!monedaConDecimales()) return Math.floor(Math.max(0, v) / 100) * 100;
  return pisoMoneda(Math.max(0, v));
}
